import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber,
  Checkbox, Row, Col, Typography, message, Popconfirm, Badge, Tooltip, Spin
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SafetyOutlined,
  LockOutlined, SaveOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { handleApiError } from '../../utils/errorHandler';
import logger from '../../services/logger';

const { Title, Text } = Typography;

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permLoading, setPermLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form] = Form.useForm();

  // Rolleri yükle
  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/roles');
      setRoles(response.data);
    } catch (error) {
      handleApiError(error, 'Roller yüklenirken hata oluştu', message.error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Tüm izinleri yükle
  const fetchAllPermissions = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/roles/permissions/all');
      setAllPermissions(response.data);
    } catch (error) {
      handleApiError(error, 'İzinler yüklenirken hata oluştu', message.error);
    }
  }, []);

  // Rol detayını yükle
  const fetchRoleDetail = useCallback(async (roleId) => {
    try {
      setPermLoading(true);
      const response = await axios.get(`/api/v1/roles/${roleId}`);
      setRolePermissions(response.data.permissions || []);
    } catch (error) {
      handleApiError(error, 'Rol detayı yüklenirken hata oluştu', message.error);
    } finally {
      setPermLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchAllPermissions();
  }, [fetchRoles, fetchAllPermissions]);

  useEffect(() => {
    if (selectedRole) {
      fetchRoleDetail(selectedRole.id);
    }
  }, [selectedRole, fetchRoleDetail]);

  // İzinleri kategorilere grupla
  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {});

  const categoryLabels = {
    user: 'Kullanıcı Yönetimi',
    device: 'Cihaz Yönetimi',
    firmware: 'Firmware (Yazılım)',
    customer: 'Müşteri Yönetimi',
    rental: 'Kiralama Yönetimi',
    billing: 'Fatura ve Ödeme',
    telemetry: 'Cihaz Verileri (Telemetri)',
    mqtt: 'MQTT Haberleşme',
    audit: 'Denetim ve Loglar',
    session: 'Oturum Yönetimi',
    system: 'Sistem Ayarları',
    github: 'GitHub Entegrasyonu',
    api: 'Harici API Erişimi'
  };

  // İzin toggle
  const handlePermissionToggle = (permValue, checked) => {
    if (checked) {
      setRolePermissions(prev => [...prev, permValue]);
    } else {
      setRolePermissions(prev => prev.filter(p => p !== permValue));
    }
  };

  // Kategori toggle (tümünü seç/kaldır)
  const handleCategoryToggle = (category, checked) => {
    const categoryPerms = groupedPermissions[category]?.map(p => p.value) || [];
    if (checked) {
      setRolePermissions(prev => [...new Set([...prev, ...categoryPerms])]);
    } else {
      setRolePermissions(prev => prev.filter(p => !categoryPerms.includes(p)));
    }
  };

  // İzinleri kaydet
  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    try {
      setSaving(true);
      await axios.put(`/api/v1/roles/${selectedRole.id}`, {
        permissions: rolePermissions
      });
      message.success('İzinler başarıyla kaydedildi');
      fetchRoles(); // User count vb. güncellenmiş olabilir
    } catch (error) {
      handleApiError(error, 'İzinler kaydedilirken hata oluştu', message.error);
    } finally {
      setSaving(false);
    }
  };

  // Rol oluştur/düzenle
  const handleRoleSubmit = async (values) => {
    try {
      if (editingRole) {
        await axios.put(`/api/v1/roles/${editingRole.id}`, values);
        message.success('Rol başarıyla güncellendi');
      } else {
        await axios.post('/api/v1/roles', { ...values, permissions: [] });
        message.success('Rol başarıyla oluşturuldu');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingRole(null);
      fetchRoles();
    } catch (error) {
      handleApiError(error, 'Rol kaydedilirken hata oluştu', message.error);
    }
  };

  // Rol sil
  const handleDeleteRole = async (roleId) => {
    try {
      await axios.delete(`/api/v1/roles/${roleId}`);
      message.success('Rol başarıyla silindi');
      if (selectedRole?.id === roleId) {
        setSelectedRole(null);
        setRolePermissions([]);
      }
      fetchRoles();
    } catch (error) {
      handleApiError(error, 'Rol silinirken hata oluştu', message.error);
    }
  };

  // Düzenle modal'ı aç
  const openEditModal = (role) => {
    setEditingRole(role);
    form.setFieldsValue({
      display_name: role.display_name,
      description: role.description,
      hierarchy_level: role.hierarchy_level,
      color: role.color,
    });
    setModalVisible(true);
  };

  // Yeni rol modal'ı aç
  const openCreateModal = () => {
    setEditingRole(null);
    form.resetFields();
    form.setFieldsValue({ hierarchy_level: 1, color: '#1890ff' });
    setModalVisible(true);
  };

  // Rol tablosu kolonları
  const columns = [
    {
      title: 'Rol',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (text, record) => (
        <Space>
          <Badge color={record.color || '#d9d9d9'} />
          <span style={{ fontWeight: selectedRole?.id === record.id ? 'bold' : 'normal' }}>
            {text}
          </span>
          {record.is_system && (
            <Tooltip title="Sistem rolü - silinemez">
              <LockOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Kod',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: 'Seviye',
      dataIndex: 'hierarchy_level',
      key: 'hierarchy_level',
      width: 80,
      align: 'center',
    },
    {
      title: 'İzinler',
      dataIndex: 'permission_count',
      key: 'permission_count',
      width: 80,
      align: 'center',
    },
    {
      title: 'Kullanıcılar',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 100,
      align: 'center',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Düzenle">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); openEditModal(record); }}
            />
          </Tooltip>
          {!record.is_system && (
            <Popconfirm
              title="Bu rolü silmek istediğinizden emin misiniz?"
              onConfirm={(e) => { if (e) e.stopPropagation(); handleDeleteRole(record.id); }}
              onCancel={(e) => { if (e) e.stopPropagation(); }}
              okText="Evet"
              cancelText="Hayır"
            >
              <Tooltip title="Sil">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                  disabled={record.user_count > 0}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const isAdminRole = selectedRole?.name === 'admin';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Rol Listesi */}
      <Card
        title={
          <Space>
            <SafetyOutlined />
            <span>Roller</span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Yeni Rol
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={roles}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          onRow={(record) => ({
            onClick: () => setSelectedRole(record),
            style: {
              cursor: 'pointer',
              backgroundColor: selectedRole?.id === record.id ? '#e6f7ff' : undefined,
            },
          })}
        />
      </Card>

      {/* Permission Matrix */}
      {selectedRole && (
        <Card
          title={
            <Space>
              <Badge color={selectedRole.color || '#d9d9d9'} />
              <span>{selectedRole.display_name} - İzin Matrisi</span>
              {isAdminRole && (
                <Tag color="red">Admin - Tüm izinler aktif</Tag>
              )}
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSavePermissions}
              loading={saving}
              disabled={isAdminRole}
            >
              İzinleri Kaydet
            </Button>
          }
        >
          {permLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              {Object.entries(groupedPermissions).map(([category, perms]) => {
                const allChecked = perms.every(p => rolePermissions.includes(p.value));
                const someChecked = perms.some(p => rolePermissions.includes(p.value));

                return (
                  <Col xs={24} sm={12} lg={8} xl={6} key={category}>
                    <Card
                      size="small"
                      title={
                        <Checkbox
                          checked={allChecked}
                          indeterminate={!allChecked && someChecked}
                          onChange={(e) => handleCategoryToggle(category, e.target.checked)}
                          disabled={isAdminRole}
                        >
                          <Text strong>{categoryLabels[category] || category}</Text>
                        </Checkbox>
                      }
                      style={{ height: '100%' }}
                    >
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {perms.map(perm => (
                            <Checkbox
                              key={perm.value}
                              checked={isAdminRole || rolePermissions.includes(perm.value)}
                              onChange={(e) => handlePermissionToggle(perm.value, e.target.checked)}
                              disabled={isAdminRole}
                            >
                              <Tooltip title={perm.value}>
                                <Text style={{ fontSize: 12 }}>{perm.label}</Text>
                              </Tooltip>
                            </Checkbox>
                          ))}
                      </Space>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card>
      )}

      {/* Rol Oluştur/Düzenle Modal */}
      <Modal
        title={editingRole ? 'Rol Düzenle' : 'Yeni Rol Oluştur'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditingRole(null); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleRoleSubmit}
        >
          {!editingRole && (
            <Form.Item
              name="name"
              label="Rol Kodu (slug)"
              rules={[
                { required: true, message: 'Rol kodu gerekli' },
                { pattern: /^[a-z][a-z0-9_]*$/, message: 'Küçük harf, rakam ve alt çizgi' }
              ]}
            >
              <Input placeholder="custom_role" />
            </Form.Item>
          )}

          <Form.Item
            name="display_name"
            label="Görünen Ad"
            rules={[{ required: true, message: 'Görünen ad gerekli' }]}
          >
            <Input placeholder="Özel Rol" />
          </Form.Item>

          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={2} placeholder="Rol açıklaması" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="hierarchy_level"
                label="Hiyerarşi Seviyesi"
                rules={[{ required: true, message: 'Seviye gerekli' }]}
              >
                <InputNumber min={0} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="color" label="Renk">
                <Input type="color" style={{ width: '100%', height: 32 }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setModalVisible(false); setEditingRole(null); }}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit">
                {editingRole ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default RolesPermissions;
